import React, { useCallback, useEffect, useState } from 'react';
import { shouldFetchData, status } from '../../data/fetchStatus';
import { ProfileContext } from './contexts';
import ClientProvider from './clientProvider';
import ExpertProvider from './expertProvider';
import Services from '../Services/services';
import useToast from '../Toasts/useToast';
import {
  ToastHeaders,
  ToastThemes,
  ToastMessages,
} from '../Toasts/ToastOptions/toastOptions';
import { Outlet, useNavigate } from 'react-router';
import { userRole } from '../../data/roleData';
import api from '../../utils/axios';

function ProfileProvider() {
  const [user, setUser] = useState(undefined);

  const [client, setClient] = useState(undefined);
  const [clientFetched, setClientFetched] = useState(status.FETCHED);
  const [expert, setExpert] = useState(undefined);
  const [expertFetched, setExpertFetched] = useState(status.FETCHED);
  const [role, setRole] = useState(userRole.UNDEFINED);

  const enqueueToast = useToast();

  const [userFetched, setUserFetched] = useState(status.FETCHED);

  const navigate = useNavigate();

  const login = async (email, password) => {
    setUserFetched(status.FETCHING);
    try {
      let loginUser = await Services.userService.login(email, password);

      api.setInstanceTokens(loginUser?.accessToken, loginUser?.refreshToken);

      delete loginUser?.accessToken;
      delete loginUser?.refreshToken;

      setUser(loginUser);
      sessionStorage.setItem('cec-user', JSON.stringify(loginUser));
      localStorage.setItem('cec-user', JSON.stringify(loginUser));
      setUserFetched(status.FETCHED);
      setClientFetched(status.REFRESH);
      setExpertFetched(status.REFRESH);

      navigate('/');

      return true;
    } catch (error) {
      console.error(error);
      const { code } = error?.response?.data || 0;

      if (code === 4000 || code === 4001 || code === 40402) {
        enqueueToast('Identifiant ou mot de passe incorrect', {
          theme: ToastThemes.toastThemes.ERROR_OCCURED,
          header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
          duration: 10000,
        });
      } else {
        enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
          theme: ToastThemes.toastThemes.ERROR_OCCURED,
          header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
          duration: 3000,
        });
      }

      setUserFetched(status.FETCHED);

      return false;
    }
  };

  const fetchUser = useCallback(async () => {
    if (userFetched === status.FETCHED || user === undefined) return;

    setUserFetched(status.FETCHING);

    try {
      const userData = await Services.userService.userByID(user.id);
      setUser(userData);
      sessionStorage.setItem('cec-user', JSON.stringify(userData));
      localStorage.setItem('cec-user', JSON.stringify(userData));
      setUserFetched(status.FETCHED);
      setClientFetched(status.REFRESH);
      setExpertFetched(status.REFRESH);
    } catch (error) {
      console.error(error);
      setUserFetched(status.FETCHED);
    }
  }, [userFetched, user]);

  const fetchClient = useCallback(async () => {
    if (user?.id === undefined || clientFetched === status.FETCHED) return;

    setClientFetched(status.FETCHING);

    try {
      const data = await Services.profileService.clientByUserID(user?.id);

      if (data.length !== 1) {
        setClientFetched(status.FETCHED);
        return;
      }

      setRole(userRole.CLIENT);
      setClient(data[0]);
      setClientFetched(status.FETCHED);
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
      setClient(undefined);
      setClientFetched(status.FETCHED);
    }
  }, [user?.id, clientFetched, enqueueToast]);

  const fetchExpert = useCallback(async () => {
    if (user?.id === undefined || expertFetched === status.FETCHED) return;

    setExpertFetched(status.FETCHING);

    try {
      const data = await Services.profileService.expertByUserID(user?.id);

      if (data.length !== 1) {
        setExpertFetched(status.FETCHED);
        return;
      }

      setRole(userRole.EXPERT);
      setExpert(data[0]);
      setExpertFetched(status.FETCHED);
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
      setExpert(undefined);
      setExpertFetched(status.FETCHED);
    }
  }, [user?.id, expertFetched, enqueueToast]);

  useEffect(() => {
    if (user !== undefined) {
      if (shouldFetchData(clientFetched)) fetchClient();

      if (shouldFetchData(expertFetched)) fetchExpert();

      if (shouldFetchData(userFetched)) fetchUser();
    }
  }, [
    clientFetched,
    expertFetched,
    userFetched,
    fetchClient,
    fetchExpert,
    fetchUser,
    user,
  ]);

  const loginFromStorage = (userItem) => {
    setUser(userItem);
    setUserFetched(status.DEFAULT);
  };

  const logout = async () => {
    setUser(undefined);
    setClient(undefined);
    setExpert(undefined);
    setRole(userRole.UNDEFINED);
    setClientFetched(status.DEFAULT);
    setExpertFetched(status.DEFAULT);
    setUserFetched(status.FETCHED);
    sessionStorage.removeItem('cec-user');
    sessionStorage.removeItem('cec-access-token');
    sessionStorage.removeItem('cec-refresh-token');
    localStorage.removeItem('cec-user');
    localStorage.removeItem('cec-access-token');
    localStorage.removeItem('cec-refresh-token');
    navigate('/login');
  };

  function shouldFetchUser() {
    setUserFetched(status.REFRESH);
  }

  function shouldFetchExpert() {
    setExpertFetched(status.REFRESH);
  }

  return (
    <ProfileContext.Provider
      value={{
        login,
        loginFromStorage,
        logout,
        fetchingUser: [
          status.DEFAULT,
          status.REFRESH,
          status.FETCHING,
        ].includes(userFetched),
        user,
        shouldFetchUser,
        isLogged: user?.id !== undefined,
        isAccountValidate:
          user?.emailValidationDate !== null &&
          user?.emailValidationDate !== undefined,
        role,
        client,
        isClient: role === userRole.CLIENT,
        expert,
        isExpert: role === userRole.EXPERT,
        shouldFetchExpert,
        fetchingClient:
          [status.DEFAULT, status.REFRESH, status.FETCHING].includes(
            clientFetched
          ) || false,
        fetchingExpert:
          [status.DEFAULT, status.REFRESH, status.FETCHING].includes(
            expertFetched
          ) || false,
      }}
    >
      {role === userRole.CLIENT && <ClientProvider />}
      {role === userRole.EXPERT && <ExpertProvider />}
      {role === userRole.UNDEFINED && <Outlet />}
    </ProfileContext.Provider>
  );
}

export default ProfileProvider;
